import React from 'react';
import { observer } from 'mobx-react';

import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

import { ActivityType } from 'utils/stores/Activities/constants';

import Avatar from 'components/Avatar/Avatar';

import ActionsIcon from 'ui/Icons/Actions';

import { DEFAULT_TIME_FORMAT } from 'core/constants';

import { formatMoment } from 'utils/formatters/timezone';
import { ADMIN_AND_ACCOUNT_MANAGERS } from 'utils/roles/roleGroups';

import {
  ItemContent,
  ItemLayout,
  ItemHeader,
  ItemHeaderData,
  ItemDate,
  UserContainer,
  UserName,
  ItemContainer,
  CommentSubtitle,
} from './styles';
import { ItemProps } from './types';

const Item: React.FunctionComponent<ItemProps> = ({
  activity,
  children,
  onDeleteItem,
  onEditItem,
  sessionUser,
  nonEditable,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | SVGSVGElement>(null);
  const [menuOpacity, setMenuOpacity] = React.useState<number>(0);

  const isCurrentUserAuthor = activity?.creator?.uuid === sessionUser.uuid;

  const canDelete = onDeleteItem && isCurrentUserAuthor;
  const canDeleteFile =
    sessionUser.role &&
    ADMIN_AND_ACCOUNT_MANAGERS.includes(sessionUser.role) &&
    activity.type === ActivityType.File &&
    onDeleteItem;

  const handleClick = (event: React.MouseEvent<SVGSVGElement>): void => {
    if (canDelete || canDeleteFile) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const onEdit = (): void => {
    handleClose();
    onEditItem && onEditItem(activity.uuid);
  };

  const onDelete = (): void => {
    handleClose();
    (canDelete || canDeleteFile) && onDeleteItem(activity.uuid);
  };

  return (
    <ItemContainer
      onMouseEnter={() => {
        if (canDelete || canDeleteFile) {
          setMenuOpacity(1);
        }
      }}
      onMouseLeave={() => {
        setMenuOpacity(0);
      }}
      reverse={isCurrentUserAuthor}
    >
      {activity.creator ? <Avatar name={activity?.creator.name} /> : <Avatar name="?" />}
      <ItemLayout $isAuthor={isCurrentUserAuthor}>
        <ItemHeader>
          <ItemHeaderData>
            <UserContainer>
              {activity.creator ? (
                <UserName $isAuthor={isCurrentUserAuthor}>{activity?.creator.name}</UserName>
              ) : (
                <UserName $isAuthor={isCurrentUserAuthor}>Undefined</UserName>
              )}
              <CommentSubtitle $isAuthor={isCurrentUserAuthor}>
                {isCurrentUserAuthor ? '(You)' : 'commented'}
              </CommentSubtitle>
            </UserContainer>
            <ItemDate $isAuthor={isCurrentUserAuthor} data-testid="feed-item-time">
              {formatMoment(activity.created, DEFAULT_TIME_FORMAT)}
            </ItemDate>
          </ItemHeaderData>
        </ItemHeader>
        <ItemContent>{children}</ItemContent>
      </ItemLayout>
      <ActionsIcon onClick={handleClick} style={{ opacity: menuOpacity }} id="feed-item-action-btn" />
      <Menu id="comment-menu" anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleClose}>
        {nonEditable && onEditItem && (
          <MenuItem id="edit-btn" onClick={onEdit}>
            Edit
          </MenuItem>
        )}
        {onDeleteItem && (isCurrentUserAuthor || canDeleteFile) && (
          <MenuItem id="delete-btn" onClick={onDelete}>
            Delete
          </MenuItem>
        )}
      </Menu>
    </ItemContainer>
  );
};

export default observer(Item);
